import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { Location } from './pages/Location';
import { Locations } from './pages/Locations';
import { Corporate } from './pages/Corporate';
import { ProductDetail } from './pages/ProductDetail';
import { PermanentInstall } from './pages/PermanentInstall';
import { LicenseeLogin } from './pages/LicenseeLogin';
import { PartnerPortal } from './pages/PartnerPortal';
import { EventWizard } from './pages/EventWizard';
import { MockupStudio } from './pages/MockupStudio';
import { ClientQuoteView } from './pages/ClientQuoteView';
import { ContractorPortal } from './pages/ContractorPortal';
import { InquiryModal } from './components/InquiryModal';
import { InquiryPayload } from './types';
import { WrapUpPortal } from './components/WrapUpPortal';
import { QuickInquiry } from './pages/QuickInquiry'; // ⚡ NEW: The Quick Form

// ⚡ IMPORT THE PORTAL
import { ClientPortal } from './components/ClientPortal';

// Wrapper to handle scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// 🛡️ THE SUBDOMAIN BOUNCER
const SubdomainRedirect = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (window.location.hostname === 'crm.quikpix.co.nz' && pathname === '/') {
      navigate('/login', { replace: true });
    }
  }, [pathname, navigate]);

  return null;
};

// ⚡ THE MAGIC LINK EXTRACTOR
const MagicLinkPortal = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  return <ClientPortal quoteId={quoteId || ''} />;
};

// ⚡ THE MAGIC LINK EXTRACTOR
const MagicLinkWrapUp = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  return <WrapUpPortal quoteId={quoteId || ''} />;
};


// 🛡️ THE LOCKDOWN LAYOUT WRAPPER 
// (We moved the main UI into this component so it can read the current URL)
const AppLayout = () => {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [inquiryData, setInquiryData] = useState<InquiryPayload | null>(null);
  
  // Get the current URL path
  const { pathname } = useLocation();

  const handleOpenQuote = (data?: InquiryPayload) => {
    setInquiryData(data || null);
    setIsQuoteOpen(true);
  };

  const handleClearInquiryData = () => {
    setInquiryData(null);
  };

  // ⚡ THE LOCKDOWN RULE: 
  // If the URL starts with any of these paths, hide the website Navigation and Footer!
  const isPortalRoute = pathname.startsWith('/staff') || 
                        pathname.startsWith('/login') || 
                        pathname.startsWith('/portal') || 
                        pathname.startsWith('/wrap-up') || 
                        pathname.startsWith('/view-quote') ||
                        pathname.startsWith('/quick-inquiry'); // ⚡ ADDED HERE

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-brand-neonPink selection:text-white">
      
      {/* 🛡️ Only show the Navbar if they are on a public website page */}
      {!isPortalRoute && <Navbar onOpenQuote={() => handleOpenQuote()} />}
      
      <main>
        <Routes>
          <Route path="/" element={<Home onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/corporate" element={<Corporate onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/event-wizard" element={<EventWizard onOpenQuote={handleOpenQuote} />} />
          <Route path="/mockup-studio" element={<MockupStudio onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/locations" element={<Locations onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/location/:city" element={<Location onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/product/permanent-install" element={<PermanentInstall onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/product/:productId" element={<ProductDetail onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/partners" element={<PartnerPortal onOpenQuote={() => handleOpenQuote()} />} />
          <Route path="/login" element={<LicenseeLogin />} />
          
          {/* PUBLIC CLIENT ROUTES */}
          <Route path="/view-quote/:quoteId" element={<ClientQuoteView />} />
          <Route path="/portal/:quoteId" element={<MagicLinkPortal />} />
          <Route path="/wrap-up/:quoteId" element={<MagicLinkWrapUp />} />
          <Route path="/quick-inquiry" element={<QuickInquiry />} /> {/* ⚡ NEW ROUTE */}

          {/* CONTRACTOR STAFF ROUTE */}
          <Route path="/staff" element={<ContractorPortal />} />
        </Routes>
      </main>

      {/* 🛡️ Only show the Footer if they are on a public website page */}
      {!isPortalRoute && <Footer />}
      
      <InquiryModal 
        isOpen={isQuoteOpen} 
        onClose={() => setIsQuoteOpen(false)} 
        initialData={inquiryData}
        onClearData={handleClearInquiryData}
      />
    </div>
  );
};

// THE MAIN APP LAUNCHER
const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <SubdomainRedirect />
      {/* Launch the Layout Wrapper we built above */}
      <AppLayout />
    </Router>
  );
};

export default App;
